import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../config/axiosConfig';
import './TabbedDashboard.css';
import { Tabs, Tab, Box, Paper } from '@mui/material';
import {
    Summarize,
    ConfirmationNumber,
    Public,
    MonetizationOn,
    BarChart,
    CalendarToday,
    Group
} from '@mui/icons-material';
import { DASHBOARD_URL, PLANE_TICKETS_URL as JOURNAL_VENTE_URL } from '../../settings/APISetting';

// Import des sections
import SyntheseActivite from './sections/SyntheseActivite.jsx';
import BilletterieSection from './sections/BilletterieSection.jsx';
import TourismeSection from './sections/TourismeSection.jsx';
import ChiffreAffairesSection from './sections/ChiffreAffairesSection.jsx';
import GraphiquesSection from './sections/GraphiquesSection.jsx';
import AnalyseTemporelle from './sections/AnalyseTemporelle.jsx';
import TOClientsSection from './sections/TOClientsSection.jsx';

// Les URLs sont maintenant importées depuis APISetting

const TabbedDashboard = () => {
    const [dashboardData, setDashboardData] = useState({});
    const [journalVenteData, setJournalVenteData] = useState([]);
    const [dashboardFetchStatus, setDashboardFetchStatus] = useState('idle');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedSection, setSelectedSection] = useState(1); // Section sélectionnée

    const { billets } = useSelector((state) => state.billets);

    const fetchDashboardData = async ({ startDate, endDate } = {}) => {
        setDashboardFetchStatus('loading');
        try {
            const urlParams = !!startDate && !!endDate ? `?startDate=${startDate}&endDate=${endDate}` : '';

            // Appels API via axiosInstance
            const [dashboardRes, journalRes] = await Promise.all([
                axiosInstance.get(`${DASHBOARD_URL}${urlParams}`),
                axiosInstance.get(`${JOURNAL_VENTE_URL}${urlParams}`)
            ]);

            setDashboardData(dashboardRes.data);
            setJournalVenteData(journalRes.data);
            setDashboardFetchStatus('success');
        } catch (error) {
            console.error('Erreur lors du chargement des données:', error);
            setDashboardFetchStatus('error');
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const handleFilter = () => {
        if (startDate && endDate) {
            // Validation: date début doit être <= date fin
            if (new Date(startDate) > new Date(endDate)) {
                alert("La date de début ne peut pas être postérieure à la date de fin.");
                return;
            }
            fetchDashboardData({ startDate, endDate });
        } else if (!startDate && !endDate) {
            fetchDashboardData();
        } else {
            alert("Veuillez sélectionner les deux dates (début et fin) pour filtrer.");
        }
    };

    const handleReset = () => {
        setStartDate(null);
        setEndDate(null);
        fetchDashboardData();
    };

    const handleChange = (event, newValue) => {
        setSelectedSection(newValue);
    };

    const sections = [
        { id: 1, name: "Synthèse activité", icon: <Summarize /> },
        { id: 2, name: "Billetterie", icon: <ConfirmationNumber /> },
        { id: 3, name: "Tourisme", icon: <Public /> },
        { id: 4, name: "Chiffre d'affaires", icon: <MonetizationOn /> },
        { id: 5, name: "Graphiques & Analyse", icon: <BarChart /> },
        { id: 6, name: "Analyse Temporelle", icon: <CalendarToday /> },
        { id: 7, name: "TO/Clients", icon: <Group /> }
    ];

    if (dashboardFetchStatus === 'loading') {
        return (
            <div className="tabbed-dashboard">
                <div className="dashboard-header">
                    <h1>📊 TRAVEL MANAGER - Analyse & Contrôle AFT</h1>
                    <div className="header-info">
                        <span>Dernière mise à jour: {new Date().toLocaleString('fr-FR')}</span>
                    </div>
                </div>

                <div className="dashboard-loading">
                    <div className="loader">
                        <div className="spinner"></div>
                        <p>Chargement des données...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (dashboardFetchStatus === 'error') {
        return (
            <div className="tabbed-dashboard">
                <div className="dashboard-header">
                    <h1>📊 TRAVEL MANAGER - Analyse & Contrôle AFT</h1>
                </div>

                <div className="dashboard-error">
                    <div className="error-content">
                        <h2>❌ Erreur de chargement</h2>
                        <p>Une erreur est survenue lors du chargement des données.</p>
                        <button onClick={() => fetchDashboardData()}>
                            🔄 Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tabbed-dashboard">
            <div className="dashboard-header">
                <h1>📊 TRAVEL MANAGER - Analyse & Contrôle AFT</h1>
                <div className="header-info">
                    <span>Dernière mise à jour: {new Date().toLocaleString('fr-FR')}</span>
                </div>
            </div>

            {/* Filtres */}
            <div className="dashboard-filters">
                <div className="date-filters">
                    <label htmlFor="start-date">📅 Date début:</label>
                    <input
                        id="start-date"
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value)}
                    />

                    <label htmlFor="end-date">📅 Date fin:</label>
                    <input
                        id="end-date"
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value)}
                    />

                    <button className="filter-button" onClick={handleFilter}>
                        🔍 Filtrer
                    </button>

                    <button className="reset-button" onClick={handleReset}>
                        🔄 Réinitialiser
                    </button>
                </div>



                {(startDate && endDate) && (
                    <div className="filter-status">
                        📅 Période: {new Date(startDate).toLocaleDateString('fr-FR')} - {new Date(endDate).toLocaleDateString('fr-FR')}
                    </div>
                )}
            </div>

            <div className="dashboard-content">


                {/* Sélecteur de sections avec Tabs MUI */}
                <Box sx={{ width: '100%', marginBottom: 3, paddingX: 2 }}>
                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Tabs
                            value={selectedSection}
                            onChange={handleChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            indicatorColor="primary"
                            textColor="primary"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    minHeight: 60,
                                    color: '#555',
                                    '&.Mui-selected': {
                                        color: '#fbc02d',
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    height: 4,
                                    borderRadius: '4px 4px 0 0',
                                    backgroundColor: '#fbc02d',
                                }
                            }}
                        >
                            {sections.map((section) => (
                                <Tab
                                    key={section.id}
                                    value={section.id}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{section.icon}</span>
                                            <span>{section.name}</span>
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>
                    </Paper>
                </Box>

                {/* Contenu dynamique selon la section */}
                <div className="section-content">
                    {selectedSection === 1 && <SyntheseActivite dashboardData={dashboardData} journalVenteData={journalVenteData} />}
                    {selectedSection === 2 && <BilletterieSection journalVenteData={journalVenteData} />}
                    {selectedSection === 3 && <TourismeSection dashboardData={dashboardData} />}
                    {selectedSection === 4 && <ChiffreAffairesSection dashboardData={dashboardData} journalVenteData={journalVenteData} billets={billets} />}
                    {selectedSection === 5 && <GraphiquesSection dashboardData={dashboardData} journalVenteData={journalVenteData} />}
                    {selectedSection === 6 && <AnalyseTemporelle dashboardData={dashboardData} startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />}
                    {selectedSection === 7 && <TOClientsSection dashboardData={dashboardData} />}
                </div>
            </div>
        </div>
    );
};

export default TabbedDashboard;

